import { COMMAND_CENTER_ROOT } from './commandCenterRoutes'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { isAuthError, isForbiddenError, type AccessCodeTargetType, type CommandCenterAccessCode } from '../../api/client'
import {
  CreateSecureAccess, issueSecureAccess, humanizeSecureAccessError,
  SECURE_ACCESS_ROOT, SECURE_TARGET_TYPES, type IssuePayload,
} from './olivium/secure-access'

/**
 * Issue-secure-access route wrapper (PHASE_2L).
 *   /square/command-center/olivium/secure-access/new
 *
 * Performs the real issue (api.commandCenter.accessCodes.create) and surfaces
 * the one-time plaintext exactly once. Honest auth/forbidden/backend states;
 * PHASE_2L target types fail honestly until their widening migration applies.
 */
export default function SquareCodeSecureAccessCreateRoute() {
  const navigate = useNavigate()
  // PHASE_2M P7 — prefill from query params (e.g. a portal's "Issue secure
  // access code"). Only a KNOWN target type is accepted; nothing invented.
  const [search] = useSearchParams()
  const qType = search.get('target_type') || ''
  const initialTargetType = SECURE_TARGET_TYPES.some((t) => t.value === qType) ? (qType as AccessCodeTargetType) : undefined
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)
  const [forbidden, setForbidden] = useState(false)
  const [issued, setIssued] = useState<{ access_code: string; code: CommandCenterAccessCode; message: string } | null>(null)

  const onSubmit = async (payload: IssuePayload) => {
    setSaving(true); setError(null); setAuthRequired(false); setForbidden(false)
    try {
      const res = await issueSecureAccess(payload)
      setIssued({ access_code: res.access_code, code: res.code, message: res.message })
    } catch (err) {
      if (isAuthError(err)) setAuthRequired(true)
      else if (isForbiddenError(err)) setForbidden(true)
      else setError(humanizeSecureAccessError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <CreateSecureAccess
      saving={saving}
      error={error}
      authRequired={authRequired}
      forbidden={forbidden}
      issued={issued}
      initialLabel={search.get('label') ?? undefined}
      initialTargetType={initialTargetType}
      initialTargetId={initialTargetType ? search.get('target_id') ?? undefined : undefined}
      initialTargetLabel={initialTargetType ? search.get('target_label') ?? undefined : undefined}
      onHome={() => navigate(COMMAND_CENTER_ROOT)}
      onBackCommandCenter={() => navigate(COMMAND_CENTER_ROOT)}
      onOpenHub={() => navigate(SECURE_ACCESS_ROOT)}
      onSubmit={onSubmit}
    />
  )
}
